import type { DemandCell } from "@/hooks/use-demands";
import { formatHours } from "./month-format";

/**
 * One Resource × month cell of the Demand grid (spec D3, D4). A closed NOS month leads
 * with actual hours (solid bar) and marks guidance with a tick; an open month leads with
 * guidance (tinted bar) and notes hours logged so far. The two figures are never summed,
 * and a missing figure renders "–", never 0.
 */
export function MonthBarCell({
  cell,
  isClosed,
  maxHours,
}: {
  cell: DemandCell;
  isClosed: boolean;
  maxHours: number;
}) {
  const lead = isClosed ? cell.actualHours : cell.guidanceHours;
  const pct = (v: number | null) =>
    v === null || maxHours <= 0 ? 0 : Math.min(100, (v / maxHours) * 100);

  const note = isClosed
    ? cell.guidanceHours !== null
      ? `of ${formatHours(cell.guidanceHours)} planned`
      : null
    : cell.actualHours !== null
      ? `${formatHours(cell.actualHours)} logged`
      : null;

  const title = [
    isClosed ? "Closed month" : "Open month",
    `Guidance: ${cell.guidanceHours === null ? "none" : `${formatHours(cell.guidanceHours)} h`}`,
    `Actual: ${cell.actualHours === null ? "none" : `${formatHours(cell.actualHours)} h`}`,
    cell.billableHours !== null ? `Billable: ${formatHours(cell.billableHours)} h` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="flex min-w-0 items-end gap-2" title={title}>
      <div className="relative h-9 w-2.5 shrink-0 overflow-hidden rounded-sm bg-muted">
        <div
          className={`absolute inset-x-0 bottom-0 rounded-sm ${isClosed ? "bg-brand-600" : "bg-info-300"}`}
          style={{ height: `${pct(lead)}%` }}
        />
        {isClosed && cell.guidanceHours !== null && (
          <div
            className="absolute inset-x-0 h-0.5 bg-foreground/70"
            style={{ bottom: `calc(${pct(cell.guidanceHours)}% - 1px)` }}
          />
        )}
      </div>
      <div className="min-w-0 leading-tight">
        <div className="font-mono text-xs font-medium tabular-nums">
          {lead === null ? "–" : `${formatHours(lead)}h`}
        </div>
        {note && <div className="truncate text-xs text-muted-foreground">{note}</div>}
      </div>
    </div>
  );
}
